import React, { FC, ReactElement, useState } from 'react'
import Error from '../../../Components/UI/Error'
import Load from '../../../Components/UI/Load'
import { useBookingEventQuery } from './Booking.generated-types'
import { ConfirmedBooking } from './ConfirmedBooking'
import { ClosedBooking } from './ClosedBooking'
import { ContactAmModal, ContactAmModalVariant } from './ContactAmModal'
import { EmptyState } from './EmptyState'
import { InProgressBooking } from './InProgressBooking'
import { NoAcceptedProposal } from './NoAcceptedProposal'
import { s } from './style'
import { WithdrawBrief } from './WithdrawBrief'
import { EventStatus, ProposalStatus } from '../../../types'

interface Props {
  selectedEventUUID?: string
  hasNoEvents: boolean
  eventsLoading: boolean
  onCancelEventSuccess: () => void
}

export const Booking: FC<Props> = ({
  selectedEventUUID,
  hasNoEvents,
  eventsLoading,
  onCancelEventSuccess,
}): ReactElement => {
  const [{ fetching, error, data }, refetchEvent] = useBookingEventQuery({
    variables: { UUID: selectedEventUUID },
    pause: !selectedEventUUID || hasNoEvents,
    requestPolicy: 'cache-and-network',
  })

  const [isModalVisible, setIsModalVisible] = useState(false)

  const handleCloseContactAmModal = (): void => setIsModalVisible(false)

  if (!eventsLoading && error) {
    return <Error error={error}></Error>
  }

  if (hasNoEvents) {
    return <EmptyState />
  }

  if (eventsLoading || (fetching && !data)) {
    return <Load load={true}></Load>
  }

  if (!data) {
    return null
  }

  const event = data.getEventByUUID

  // If a proposal is already booked, the user shouldn't be able to be select other proposals
  const requestedProposal =
    data == null
      ? null
      : event.proposals.find((p) => p.status === ProposalStatus.RequestedToBook)

  /*
   * If the event has been completed, closed or lost but no proposals have been
   * requested to book, the status of the booking is an 'unknown' status. There is no information about which proposal
   * was booked and so the majority of UI is not possible to display. This situation is therefore specifically handled.
   */
  const isBookingUnknown: boolean =
    !requestedProposal &&
    (event.status === EventStatus.Completed ||
      event.status === EventStatus.Closed ||
      event.status === EventStatus.Lost)

  if (isBookingUnknown) {
    return (
      <NoAcceptedProposal accountManagerEmail={event.accountManager?.email} />
    )
  }

  if (event.status === EventStatus.Closed) {
    return <ClosedBooking event={event} proposal={requestedProposal} />
  }

  if (
    event.status === EventStatus.RequestedToBook ||
    event.status === EventStatus.Completed
  ) {
    return <ConfirmedBooking event={event} proposal={requestedProposal} />
  }

  return (
    <div style={s.root}>
      <ContactAmModal
        variant={ContactAmModalVariant.Cancel}
        visible={isModalVisible}
        handleClose={handleCloseContactAmModal}
      />
      <InProgressBooking
        event={event}
        requestedProposal={requestedProposal}
        refetchEvent={refetchEvent}
      />
      <WithdrawBrief event={event} onCancelSuccess={onCancelEventSuccess} />
    </div>
  )
}
